import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as api from '../api/client'
import { useAuthStore } from '../store/auth'
import type { OwnedSessionInfo } from '../types'

export default function MySessions() {
  const user = useAuthStore((s) => s.user)
  const loading = useAuthStore((s) => s.loading)
  const navigate = useNavigate()

  // If we know the user is signed out, bounce to lobby. Wait for hydration
  // first so we don't bounce a still-loading session.
  useEffect(() => {
    if (!loading && !user) navigate('/', { replace: true })
  }, [loading, user, navigate])

  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-sessions', 'all'],
    queryFn: () => api.listMySessions(),
    enabled: !!user,
  })

  const deleteMut = useMutation({
    mutationFn: (sessionId: string) => api.deleteSession(sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-sessions'] })
    },
  })

  function handleDelete(session: OwnedSessionInfo) {
    const warn = session.status === 'active'
      ? `Delete "${session.name}"? This session is still active — `
        + `any team currently playing will get an error. This can't be undone.`
      : `Delete "${session.name}"? All decisions, snapshots, and events `
        + `for this session will be removed. This can't be undone.`
    if (window.confirm(warn)) deleteMut.mutate(session.id)
  }

  if (loading || (!user && !loading)) {
    return <div className="max-w-4xl mx-auto p-8 text-sm text-slate-500">Loading…</div>
  }

  return (
    <div className="max-w-4xl mx-auto p-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Your sessions</h1>
        <p className="text-sm text-slate-600 mt-1">
          Every session you've created while signed in. Anonymous sessions
          are not listed.
        </p>
      </header>

      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}
      {error && <div className="text-sm text-danger">Could not load sessions.</div>}

      {data && data.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600">
          You haven't created any sessions yet.{' '}
          <Link to="/" className="text-brand-700 hover:underline">
            Create one →
          </Link>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Quarter</th>
                <th className="text-left px-4 py-2 font-medium">Last activity</th>
                <th className="text-left px-4 py-2 font-medium">Created</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((s) => (
                <SessionRow key={s.id} session={s} onDelete={handleDelete} deleting={deleteMut.isPending && deleteMut.variables === s.id} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function SessionRow({
  session, onDelete, deleting,
}: {
  session: OwnedSessionInfo
  onDelete: (s: OwnedSessionInfo) => void
  deleting: boolean
}) {
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-2 font-medium text-slate-800">{session.name}</td>
      <td className="px-4 py-2">
        <StatusBadge status={session.status} />
      </td>
      <td className="px-4 py-2 text-slate-700">
        Q{session.current_quarter} / {session.num_quarters}
      </td>
      <td className="px-4 py-2 text-slate-500">
        {formatRelative(session.last_activity_at ?? session.created_at)}
      </td>
      <td className="px-4 py-2 text-slate-500">
        {new Date(session.created_at * 1000).toLocaleDateString()}
      </td>
      <td className="px-4 py-2 text-right whitespace-nowrap">
        <Link
          to={`/facilitator/${session.id}`}
          className="text-brand-700 hover:underline"
        >
          Open →
        </Link>
        <button
          type="button"
          onClick={() => onDelete(session)}
          disabled={deleting}
          className="ml-4 text-slate-400 hover:text-danger disabled:opacity-50"
          aria-label={`Delete ${session.name}`}
          title="Delete session"
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </td>
    </tr>
  )
}

function StatusBadge({ status }: { status: 'lobby' | 'active' | 'complete' }) {
  const styles: Record<typeof status, string> = {
    lobby:    'bg-slate-100 text-slate-700',
    active:   'bg-brand-100 text-brand-700',
    complete: 'bg-green-100 text-green-700',
  }
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  )
}

function formatRelative(ts: number): string {
  const seconds = Date.now() / 1000 - ts
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 86400 * 7) return `${Math.floor(seconds / 86400)}d ago`
  return new Date(ts * 1000).toLocaleDateString()
}
